/**
 * Where an employee's entitlement figures came from — the Preferred Creditors
 * equivalent of the creditors panel's estimates list.
 *
 * Purpose: an employee can have several competing views of what they are owed —
 * what the payroll records show, what they claimed on their proof of debt, what
 * was admitted on adjudication, and the practitioner's own figure. This section
 * lists them as cards so the total above is never an unexplained number.
 *
 * Unlike the creditors panel, endorsing a card does not move the register: an
 * employee's entitlements are a per-category breakdown FEG depends on, and a
 * proof of debt states one figure with no split. So the recorded card is the
 * figure of record, and a claimed figure can only be adopted while the
 * breakdown is still blank — after that the categories are edited directly.
 *
 * @component
 * @example
 * <EntitlementEstimatesSection entry={entry} isViewer={isViewer} />
 */

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useConvexMutation, convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { ArrowDownToLine, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { cn } from "@/utils/misc";
import type { EmployeeEntryUI, EntitlementEstimateUI } from "./types";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const AUD = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

function fmtDate(iso?: string): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** Single-character provenance badge, mirroring the creditor estimate cards. */
const SOURCE_INITIAL: Record<EntitlementEstimateUI["source"], string> = {
  document: "D",
  proof_of_debt: "P",
  pod_admitted: "A",
  manual: "M",
};

const SOURCE_BADGE_CLASS: Record<EntitlementEstimateUI["source"], string> = {
  document: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
  proof_of_debt:
    "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  pod_admitted:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  manual:
    "bg-violet-100 text-violet-700 dark:bg-violet-950 dark:text-violet-400",
};

/** Parses a typed figure, tolerating pasted values like "$1,200.00". */
function parseAmount(raw: string): number | null {
  const cleaned = raw.trim().replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const parsed = Number(cleaned);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return parsed;
}

// ---------------------------------------------------------------------------
// Estimate card
// ---------------------------------------------------------------------------

function EstimateCard({
  estimate,
  recorded,
  canEdit,
  canAdopt,
  onUpdateAmount,
  onUpdateAsAt,
  onDelete,
  onAdopt,
}: {
  estimate: EntitlementEstimateUI;
  /** The figure the register holds, so a card can say how it differs. */
  recorded: number;
  canEdit: boolean;
  /** Whether adopting a figure is possible — only while nothing is recorded. */
  canAdopt: boolean;
  onUpdateAmount: (amount: number) => void;
  /** An empty string clears the date. */
  onUpdateAsAt: (isoDate: string) => void;
  onDelete: () => void;
  onAdopt: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [draft, setDraft] = useState(String(estimate.amount));

  // The recorded card mirrors the categories below it, and a POD figure belongs
  // to the claim — both are changed where they live, not here.
  const canAmend = canEdit && !estimate.isLive && !estimate.isRecorded;
  const asAt = fmtDate(estimate.asAtDate);
  const variance = estimate.amount - recorded;
  const showAdopt = canEdit && canAdopt && !estimate.isRecorded;

  function commit() {
    const parsed = parseAmount(draft);
    if (parsed != null && parsed !== estimate.amount) onUpdateAmount(parsed);
    setEditing(false);
  }

  return (
    <div
      className={cn(
        "group rounded-lg border p-3 transition-colors",
        estimate.isRecorded
          ? "border-emerald-500 bg-emerald-50/30 dark:bg-emerald-950/20"
          : "border-border hover:border-muted-foreground/30",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div
            className={cn(
              "flex h-6 w-6 shrink-0 items-center justify-center rounded text-[10px] font-bold",
              SOURCE_BADGE_CLASS[estimate.source],
            )}
          >
            {SOURCE_INITIAL[estimate.source]}
          </div>
          <span className="truncate text-xs font-semibold text-foreground">
            {estimate.label ?? "Estimate"}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {asAt ? (
            <span
              className={cn(
                "whitespace-nowrap text-[10px] text-muted-foreground",
                canAmend && "cursor-text hover:text-foreground",
              )}
              title={canAmend ? "Change the as-at date" : undefined}
              onClick={() => canAmend && setEditingDate(true)}
            >
              {asAt}
            </span>
          ) : (
            canAmend && (
              <button
                type="button"
                onClick={() => setEditingDate(true)}
                className="whitespace-nowrap text-[10px] text-muted-foreground/60 hover:text-foreground"
              >
                Set date
              </button>
            )
          )}
          {canAmend && (
            <button
              type="button"
              onClick={onDelete}
              className="rounded p-0.5 text-muted-foreground/0 transition-colors hover:bg-muted group-hover:text-muted-foreground"
              title="Remove this estimate"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        {editing && canAmend ? (
          <div className="flex items-center gap-2">
            <Input
              inputMode="decimal"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setDraft(String(estimate.amount));
                  setEditing(false);
                }
              }}
              className="h-7 w-36 text-sm font-semibold tabular-nums"
              autoFocus
            />
            <Button size="sm" className="h-7 text-xs" onClick={commit}>
              Save
            </Button>
          </div>
        ) : (
          <span
            className={cn(
              "text-sm font-bold tabular-nums",
              estimate.isRecorded
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-foreground",
              canAmend && "cursor-text",
            )}
            onClick={() => {
              if (!canAmend) return;
              setDraft(String(estimate.amount));
              setEditing(true);
            }}
          >
            {AUD.format(estimate.amount)}
            {canAmend && (
              <Pencil className="ml-1 inline-block h-2.5 w-2.5 opacity-30" />
            )}
          </span>
        )}

        {showAdopt && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 text-xs"
            onClick={onAdopt}
            title="Record this figure as unpaid wages, then split it across the categories"
          >
            <ArrowDownToLine className="h-3 w-3" />
            Record
          </Button>
        )}
      </div>

      {editingDate && canAmend && (
        <div className="mt-2">
          <DatePicker
            value={estimate.asAtDate ?? ""}
            onChange={(iso) => {
              onUpdateAsAt(iso);
              setEditingDate(false);
            }}
            placeholder="As at"
            className="w-44"
            inputClassName="h-8 pl-7 pr-7 text-xs tabular-nums border-input rounded-md bg-background"
          />
        </div>
      )}

      {!estimate.isRecorded && recorded > 0 && variance !== 0 && (
        <p className="mt-2 text-[10px] text-muted-foreground">
          {AUD.format(Math.abs(variance))}{" "}
          {variance > 0 ? "more than" : "less than"} the recorded entitlements.
        </p>
      )}
      {estimate.notes && (
        <p className="mt-2 whitespace-pre-line text-xs text-muted-foreground">
          {estimate.notes}
        </p>
      )}
      {estimate.sourceDocument && (
        <p className="mt-2 truncate text-[10px] text-muted-foreground">
          {estimate.sourceDocument.fileName}
        </p>
      )}
      {estimate.isRecorded && (
        <p className="mt-2 text-[10px] text-muted-foreground">
          The figures on the register — edit the categories below to change
          them.
        </p>
      )}
      {estimate.isLive && (
        <p className="mt-2 text-[10px] text-muted-foreground">
          From the proof of debt — edit the claim to change it.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add form
// ---------------------------------------------------------------------------

function AddEstimateForm({
  onAdd,
  onCancel,
}: {
  onAdd: (input: { amount: number; asAtDate?: string; notes?: string }) => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [asAt, setAsAt] = useState("");
  const [notes, setNotes] = useState("");

  const parsed = parseAmount(amount);

  return (
    <div className="space-y-3 rounded-lg border-2 border-dashed border-violet-200 p-4 dark:border-violet-800">
      <p className="text-sm font-semibold text-violet-700 dark:text-violet-400">
        Add practitioner estimate
      </p>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Total owed ($)
        </label>
        <Input
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          className="h-9 text-sm tabular-nums"
          autoFocus
        />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          As at
        </label>
        <DatePicker value={asAt} onChange={setAsAt} className="w-full" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Notes
        </label>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Basis for this estimate..."
          rows={2}
          className="text-sm"
        />
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1"
          disabled={parsed == null}
          onClick={() => {
            if (parsed == null) return;
            onAdd({
              amount: parsed,
              asAtDate: asAt.trim() || undefined,
              notes: notes.trim() || undefined,
            });
          }}
        >
          Add estimate
        </Button>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

export function EntitlementEstimatesSection({
  entry,
  /** The register's own total, so each card can be read against it. */
  recordedTotal,
  isViewer,
}: {
  entry: EmployeeEntryUI;
  recordedTotal: number;
  isViewer?: boolean;
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const canEdit = !isViewer;
  const entryId = entry._id as Id<"employeeEntries">;

  const { data } = useQuery(
    convexQuery(
      api.jobs.summarization.agents.employment.publicQueries
        .listEmployeeEntitlementEstimates,
      { entryId },
    ),
  );
  const cards = data ?? [];

  const addEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations
        .addEmployeeEntitlementEstimate,
    ),
  });
  const updateEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations
        .updateEmployeeEntitlementEstimate,
    ),
  });
  const deleteEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations
        .deleteEmployeeEntitlementEstimate,
    ),
  });
  const applyEstimate = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.summarization.agents.employment.publicMutations
        .applyEntitlementEstimate,
    ),
  });

  return (
    <>
      <div className="flex items-center justify-between pb-1.5 pt-6">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium uppercase tracking-widest text-[#121212] dark:text-foreground">
            Estimates
          </span>
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
            {cards.length}
          </span>
        </div>
        {canEdit && !showAddForm && (
          <Button
            variant="outline"
            size="sm"
            className="h-7 gap-1 border-violet-200 text-xs text-violet-600 dark:border-violet-800 dark:text-violet-400"
            onClick={() => setShowAddForm(true)}
          >
            <Plus className="h-3 w-3" />
            Add estimate
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {cards.map((estimate) => (
          <EstimateCard
            key={estimate.id}
            estimate={estimate}
            recorded={recordedTotal}
            canEdit={canEdit}
            canAdopt={recordedTotal === 0}
            onUpdateAmount={(amount) =>
              updateEstimate.mutate({
                entryId,
                estimateId: estimate.id,
                amount,
              })
            }
            onUpdateAsAt={(isoDate) =>
              updateEstimate.mutate({
                entryId,
                estimateId: estimate.id,
                ...(isoDate ? { asAtDate: isoDate } : { clearAsAtDate: true }),
              })
            }
            onDelete={() =>
              deleteEstimate.mutate({ entryId, estimateId: estimate.id })
            }
            onAdopt={() =>
              applyEstimate.mutate(
                { entryId, estimateId: estimate.id },
                {
                  onSuccess: () =>
                    toast.success(
                      "Recorded as unpaid wages — split it across the categories below.",
                    ),
                  onError: (e) =>
                    toast.error(
                      e instanceof Error
                        ? e.message
                        : "Could not record that figure",
                    ),
                },
              )
            }
          />
        ))}

        {cards.length === 0 && !showAddForm && (
          <p className="py-4 text-center text-sm text-muted-foreground">
            No figures yet.
          </p>
        )}

        {showAddForm && (
          <AddEstimateForm
            onAdd={(input) => {
              addEstimate.mutate({ entryId, ...input });
              setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        )}
      </div>
    </>
  );
}
